import { A, D, S, pipe } from '@mobily/ts-belt';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { useRouter, NextRouter } from 'next/router';
type LinkProps = NextLinkProps & {
  children: React.ReactNode;
};

/** the __sfndp is used for sf-next deploy previews. */
const approvedQueryParams = ['__sfndp', 'source', 'sub_source'] as const;

/**
 * Adding path query params in the url to the href.
 * The primary use of this is to preserve deploy preview links.
 * It will also allow any marketing campaign params to flow through the app.
 */
export const processHref = (href: LinkProps['href'], query?: NextRouter['query']) => {
  // Return early if query is undefined. We do this as a sensible fallback,
  // so consumers do not have to mock next router in all tests that use `Link`,
  // which would be tedious.
  if (!query || !href) {
    return href;
  }
  const filteredQueryParams = D.filterWithKey(query, key => A.includes(approvedQueryParams, key) || S.startsWith(String(key), 'utm_'));
  if (D.isEmpty(filteredQueryParams)) {
    return href;
  }

  // creates a string of query params to append to the href
  const queryParamsString = pipe(filteredQueryParams, D.toPairs, A.map(([key, value]) => `${key}=${value}`), A.join('&'));

  // regex pattern used below to check for a question mark
  const matchQueryParams = /\?(.*)/;

  // Check if href already has query string
  const hasQueryParams = `${href}`.match(matchQueryParams)?.[1];

  // Format and join href and url query params
  return `${href}${hasQueryParams ? '&' : '?'}${queryParamsString}`;
};
const Link = ({
  children,
  href,
  replace,
  scroll,
  shallow,
  prefetch,
  ...props
}: LinkProps) => {
  const router = useRouter();
  return <NextLink href={processHref(href, router?.query)} replace={replace} scroll={scroll} shallow={shallow} prefetch={prefetch} passHref legacyBehavior>
      <a {...props}>{children}</a>
    </NextLink>;
};
export default Link;